// Benign-browser-noise filter for PostHog Error Tracking.
// Reads $exception_list (array of ExceptionValue objects) which is the
// client-side shape; $exception_values as a string array is server-derived
// during ingest and is NOT present at `before_send` time.

import type { CaptureResult } from 'posthog-js';
import { redactSensitiveParamsInString, redactedCopy } from './urlRedaction';

const BENIGN_MESSAGE_SUBSTRINGS: readonly string[] = [
  // Chrome/Firefox browser quirk — fires when a ResizeObserver callback
  // triggers another layout pass in the same frame. Harmless.
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',

  // Cross-origin script errors are sanitized by browsers to the literal
  // string "Script error." with no stack. Zero actionable info.
  'Script error.',

  // Non-Error values dispatched via window.dispatchEvent(new CustomEvent(...))
  // being wrongly picked up by posthog's global handler. The SDK formats
  // these as `${constructorName} captured as exception with keys: ...`
  // (see @posthog/core/error-tracking/coercers/event-coercer.ts) — confirmed
  // empirically as "CustomEvent captured as exception with keys: isTrusted".
  'CustomEvent captured as exception',

  // Browser extension postMessage channels (LastPass, 1Password, etc.).
  // "relay-message-to-frames" is the canonical signature; the broader
  // "Unhandled error response received for message" is a backstop for
  // related extension wrappers we observed with different prefixes.
  'relay-message-to-frames',
  'Unhandled error response received for message',

  // Transient network failures during script tag loading — user refresh
  // recovers, nothing we can fix.
  'Unexpected end of script',
];

function getExceptionMessages(event: CaptureResult): readonly string[] {
  const list = event.properties?.['$exception_list'];
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) =>
      typeof entry === 'object' && entry !== null
        ? (entry as Record<string, unknown>)['value']
        : undefined
    )
    .filter((v): v is string => typeof v === 'string');
}

/**
 * Returns true if the exception matches a known-benign pattern and should
 * NOT be sent to PostHog. Called from posthog.init's `before_send` hook.
 */
export function isBenignException(event: CaptureResult): boolean {
  if (event.event !== '$exception') return false;

  const messages = getExceptionMessages(event);
  if (messages.length === 0) return false;

  return messages.some((msg) =>
    BENIGN_MESSAGE_SUBSTRINGS.some((needle) => msg.includes(needle))
  );
}

// UUIDs in URL path segments (e.g. /sk/chat/001a28dc-1cef-442f-aec9-c4f588451a4b)
// are pseudonymous session/document IDs. We replace them with the placeholder
// "[id]" so PostHog receives clean page patterns (/sk/chat/[id]) that can be
// aggregated, instead of per-session rows — GDPR data-minimization
// (Art. 5(1)(c)) and SAK client-confidentiality hygiene.
const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Replace UUID path segments in a URL string with "[id]", keeping the rest
 * of the URL (origin, non-UUID segments, query string, fragment) intact.
 *
 * Handles both absolute URLs (https://lexomat.sk/sk/chat/<uuid>) and relative
 * paths (/sk/chat/<uuid>) — PostHog's $pathname is a relative path and is
 * not parseable by `new URL()` without a base.
 */
export function sanitizeUrlPathSegments(url: string): string {
  // Fast path: no UUID present — skip parsing entirely.
  if (!UUID_PATTERN.test(url)) {
    UUID_PATTERN.lastIndex = 0; // reset stateful regex after test()
    return url;
  }
  UUID_PATTERN.lastIndex = 0;

  // Absolute URL: parse properly so we only mutate the pathname, not the
  // query string or fragment (which could coincidentally contain UUID-shaped
  // values we want to keep, e.g. a correlation-id query param).
  try {
    const parsed = new URL(url);
    parsed.pathname = parsed.pathname.replace(UUID_PATTERN, '[id]');
    UUID_PATTERN.lastIndex = 0;
    return parsed.toString();
  } catch {
    // Relative path (e.g. $pathname = "/sk/chat/<uuid>"): apply the regex
    // directly. The path should not contain query-string or fragment UUIDs
    // in PostHog's $pathname field — those live in $current_url.
    UUID_PATTERN.lastIndex = 0;
    return url.replace(UUID_PATTERN, '[id]');
  }
}

const ABSOLUTE_URL = /^https?:\/\//i;
const PATHNAME_KEY = /pathname$/i;

/**
 * The per-string rule for a PostHog event: anonymise UUID path segments in
 * URL-shaped strings (absolute URLs anywhere, relative paths under a
 * `*pathname` key), then redact sensitive URL params. A UUID that is not in
 * a URL path (`uuid`, `$session_id`, `distinct_id`, ...) is left alone: those
 * are PostHog's own identifiers, not page paths.
 */
function sanitizeEventString(value: string, key: string | null): string {
  const urlShaped =
    ABSOLUTE_URL.test(value) || (key !== null && PATHNAME_KEY.test(key));
  const pathClean = urlShaped ? sanitizeUrlPathSegments(value) : value;
  return redactSensitiveParamsInString(pathClean);
}

/**
 * `before_send` body: returns the event with sensitive URL params (`token`,
 * query or fragment) removed from EVERY string it carries (properties,
 * `$set`, `$set_once`, ...) and UUID path segments anonymised in every
 * URL-shaped string, in ONE walk. The event PostHog passed in is never
 * written to (it may hold frozen or caller-owned objects): a clean event is
 * returned as is, otherwise a redacted copy. Returns `null` (PostHog drops
 * the event) when it cannot be copied safely: fail closed, never send it
 * unredacted.
 *
 * The walk covers the whole event, not a list of keys, because posthog-js
 * (1.396.3) copies the entry URL into properties it builds by name at
 * runtime: `$session_entry_url` / `$session_entry_referrer` (from the session
 * props, `"$session_entry_" + key`), `$initial_current_url` /
 * `$initial_referrer` / `$initial_pathname` (`"$initial_" + key`, sent in
 * `$set_once`), and the raw `$initial_person_info: { r, u }` super property.
 * A key list misses the next one the SDK adds.
 */
export function redactUrlProperties(
  event: CaptureResult
): CaptureResult | null {
  try {
    return redactedCopy(event, sanitizeEventString);
  } catch {
    // RedactionRefusedError, or anything thrown while reading the event (a
    // throwing getter, a revoked proxy).
    return null;
  }
}
